import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import TabelaOrdensConta from '../components/TabelaOrdensConta';
import { useAuth } from '../contexts/AuthContext';
import { infoSpyPjService } from '../services/api';
import '../styles/InfoSpyPj.css';

const HISTORICO_POLL_MS = 5000;

const formatCnpj = (cnpj) => {
  const n = String(cnpj || '').replace(/\D/g, '');
  if (n.length === 11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (n.length !== 14) return cnpj || '—';
  return n.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('pt-BR');
};

const statusConfig = (status) => {
  const map = {
    pending: { label: 'Na fila', class: 'ispjp-status-pending' },
    processing: { label: 'Processando', class: 'ispjp-status-processing' },
    completed: { label: 'Concluído', class: 'ispjp-status-completed' },
    error: { label: 'Erro', class: 'ispjp-status-error' }
  };
  return map[status] || map.pending;
};

function Campo({ label, valor }) {
  return (
    <div className="ispjp-field">
      <label>{label}</label>
      <span>{valor === null || valor === undefined || valor === '' ? '—' : String(valor)}</span>
    </div>
  );
}

function ResultadoDetalhe({ resultado, consulta }) {
  if (!resultado) {
    return (
      <p style={{ color: 'var(--texto-secundario)' }}>
        Sem dados de resultado para esta consulta.
      </p>
    );
  }
  const conta = resultado.conta || {};
  const ordem = resultado.ordem || {};
  const itens = resultado.itens || [];
  const atividades = resultado.atividades || [];
  const campos = ordem.campos || {};
  const ordemAberta = Boolean(String(ordem.numero || '').trim());

  return (
    <>
      <div className="ispjp-section-title">Conta</div>
      <div className="ispjp-field-grid">
        <Campo label="Nome" valor={conta.nome} />
        <Campo label="Documento" valor={formatCnpj(conta.documento)} />
        <Campo label="Endereço" valor={conta.endereco} />
        <Campo label="Celular" valor={conta.telefoneCel} />
        <Campo label="Comercial" valor={conta.telefoneCom} />
        <Campo label="E-mail" valor={conta.email} />
      </div>
      <TabelaOrdensConta ordens={resultado.ordens} ordemPedida={consulta?.ordem} />
      {ordemAberta && (
        <>
          <div className="ispjp-section-title">Ordem</div>
          <div className="ispjp-field-grid">
            <Campo label="Número" valor={ordem.numero} />
            <Campo label="Status" valor={ordem.status} />
            <Campo label="Tipo" valor={ordem.tipo} />
            <Campo label="Pacote" valor={resultado.pacote} />
          </div>
          {Object.keys(campos).length > 0 && (
            <div className="ispjp-field-grid" style={{ marginTop: '0.75rem' }}>
              {Object.entries(campos).map(([k, v]) => (
                <Campo key={k} label={k} valor={v} />
              ))}
            </div>
          )}
        </>
      )}
      {itens.length > 0 && (
        <>
          <div className="ispjp-section-title">Itens ({itens.length})</div>
          <div className="ispjp-table-wrap">
            <table className="ispjp-table">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>Preço</th>
                  <th>Status</th>
                  <th>Promoção</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item, i) => (
                  <tr key={i}>
                    <td>
                      {item.produto || '—'}
                      {item.pai ? (
                        <div style={{ fontSize: '0.8rem', color: 'var(--texto-secundario)' }}>
                          {item.pai}
                        </div>
                      ) : null}
                    </td>
                    <td>{item.precoItem || item.precoBase || '—'}</td>
                    <td>{item.status || '—'}</td>
                    <td>{item.promocao || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {atividades.length > 0 && (
        <>
          <div className="ispjp-section-title">Atividades ({atividades.length})</div>
          <div className="ispjp-table-wrap">
            <table className="ispjp-table">
              <thead>
                <tr>
                  <th>Atividade</th>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th>Período</th>
                  <th>Endereço</th>
                  <th>Referência</th>
                </tr>
              </thead>
              <tbody>
                {atividades.map((a, i) => (
                  <tr key={i}>
                    <td>{a.atividade || '—'}</td>
                    <td>{a.tipo || '—'}</td>
                    <td>{a.status || '—'}</td>
                    <td>{a.dataPeriodo || a.dataPeriodoCf || '—'}</td>
                    <td>{a.endereco || '—'}</td>
                    <td>{a.referencia || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

const HistoricoInfoSpyPj = () => {
  const { isAdminOrDiretor } = useAuth();
  const [consultas, setConsultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({
    cnpj: '',
    ordem: '',
    status: '',
    data_inicio: '',
    data_fim: ''
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [liveUpdating, setLiveUpdating] = useState(false);

  const fetchConsultas = useCallback(
    async (options = {}) => {
      const silent = Boolean(options.silent);
      if (!silent) setLoading(true);
      else setLiveUpdating(true);
      try {
        const clean = {};
        if (filters.cnpj) clean.cnpj = filters.cnpj.replace(/\D/g, '');
        if (filters.ordem) clean.ordem = filters.ordem.trim();
        if (filters.status) clean.status = filters.status;
        if (filters.data_inicio) clean.data_inicio = filters.data_inicio;
        if (filters.data_fim) clean.data_fim = filters.data_fim;

        const response = await infoSpyPjService.getHistorico(clean, page, 20);
        if (response.success) {
          setConsultas(response.data.consultas);
          setTotal(response.data.total);
          setTotalPages(response.data.totalPages);
          if (!silent) setError('');
        }
      } catch (err) {
        if (!silent) setError(err.response?.data?.error || 'Erro ao carregar histórico');
      } finally {
        if (!silent) setLoading(false);
        else setLiveUpdating(false);
      }
    },
    [filters, page]
  );

  useEffect(() => {
    fetchConsultas();
  }, [fetchConsultas]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      fetchConsultas({ silent: true });
    };
    const id = setInterval(tick, HISTORICO_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchConsultas]);

  useEffect(() => {
    if (!showModal) return;
    setSelected((prev) => {
      if (!prev) return prev;
      return consultas.find((c) => c.id === prev.id) || prev;
    });
  }, [consultas, showModal]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleCnpjChange = (e) => {
    const n = e.target.value.replace(/\D/g, '').slice(0, 14);
    const formatted = n
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
    setFilters((prev) => ({ ...prev, cnpj: formatted }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchConsultas();
  };

  const handleClearFilters = () => {
    setFilters({ cnpj: '', ordem: '', status: '', data_inicio: '', data_fim: '' });
    setPage(1);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta consulta do histórico?')) return;
    try {
      await infoSpyPjService.deleteConsulta(id);
      setConsultas((prev) => prev.filter((c) => c.id !== id));
      setShowModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir');
    }
  };

  const formatPosicao = (c) => {
    if (!['pending', 'processing'].includes(c.status)) return '—';
    if (c.posicao_fila != null && c.posicao_fila !== '') return `#${c.posicao_fila}`;
    return '…';
  };

  return (
    <Layout>
      <div className="ispjp-container">
        <div className="ispjp-header">
          <div>
            <h1 className="ispjp-title">
              <div className="ispjp-title-icon">◈</div>
              Histórico InfoSpyPj
            </h1>
            <p className="ispjp-subtitle">
              {isAdminOrDiretor()
                ? 'Todas as consultas de ordem (dados do Siebel)'
                : 'Suas consultas e, se supervisor, da sua equipe'}
              {liveUpdating ? ' · atualizando…' : ''}
            </p>
          </div>
          <div>
            <Link to="/infospypj" className="ispjp-btn ispjp-btn-primary">
              Nova consulta
            </Link>
          </div>
        </div>

        <div className="ispjp-card">
          <div className="ispjp-card-header">
            <div className="ispjp-card-header-left">
              <span className="ispjp-card-header-title">Filtros</span>
            </div>
          </div>
          <div className="ispjp-card-body">
            <form onSubmit={handleFilterSubmit}>
              <div className="ispjp-form" style={{ flexWrap: 'wrap' }}>
                <div className="ispjp-form-group" style={{ minWidth: '180px' }}>
                  <label className="ispjp-form-label">CNPJ</label>
                  <input
                    type="text"
                    name="cnpj"
                    value={filters.cnpj}
                    onChange={handleCnpjChange}
                    placeholder="00.000.000/0000-00"
                    className="ispjp-form-input"
                  />
                </div>
                <div className="ispjp-form-group" style={{ minWidth: '180px' }}>
                  <label className="ispjp-form-label">Ordem</label>
                  <input
                    type="text"
                    name="ordem"
                    value={filters.ordem}
                    onChange={handleFilterChange}
                    placeholder="8-..."
                    className="ispjp-form-input"
                  />
                </div>
                <div className="ispjp-form-group" style={{ minWidth: '150px' }}>
                  <label className="ispjp-form-label">Status</label>
                  <select
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                    className="ispjp-form-input"
                  >
                    <option value="">Todos</option>
                    <option value="pending">Na fila</option>
                    <option value="processing">Processando</option>
                    <option value="completed">Concluído</option>
                    <option value="error">Erro</option>
                  </select>
                </div>
                <div className="ispjp-form-group" style={{ minWidth: '150px' }}>
                  <label className="ispjp-form-label">Data início</label>
                  <input
                    type="date"
                    name="data_inicio"
                    value={filters.data_inicio}
                    onChange={handleFilterChange}
                    className="ispjp-form-input"
                  />
                </div>
                <div className="ispjp-form-group" style={{ minWidth: '150px' }}>
                  <label className="ispjp-form-label">Data fim</label>
                  <input
                    type="date"
                    name="data_fim"
                    value={filters.data_fim}
                    onChange={handleFilterChange}
                    className="ispjp-form-input"
                  />
                </div>
                <button type="submit" className="ispjp-btn ispjp-btn-primary">
                  Filtrar
                </button>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="ispjp-btn ispjp-btn-secondary"
                >
                  Limpar
                </button>
              </div>
            </form>
          </div>
        </div>

        {error && <div className="ispjp-error">{error}</div>}

        <div className="ispjp-card">
          <div className="ispjp-card-header">
            <div className="ispjp-card-header-left">
              <span className="ispjp-card-header-title">
                Consultas ({total})
              </span>
            </div>
          </div>
          <div className="ispjp-card-body">
            {loading ? (
              <div className="ispjp-loading">
                <div className="ispjp-spinner" />
                Carregando…
              </div>
            ) : consultas.length === 0 ? (
              <p style={{ color: 'var(--texto-secundario)', margin: 0 }}>
                Nenhuma consulta encontrada.
              </p>
            ) : (
              <>
                <div className="ispjp-table-wrap">
                  <table className="ispjp-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>CNPJ</th>
                        <th>Ordem</th>
                        <th>Status</th>
                        <th>Fila</th>
                        <th>Usuário</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {consultas.map((c) => {
                        const st = statusConfig(c.status);
                        return (
                          <tr key={c.id}>
                            <td>{formatDate(c.created_at)}</td>
                            <td>{formatCnpj(c.cnpj)}</td>
                            <td>{c.ordem}</td>
                            <td>
                              <span className={`ispjp-status-pill ${st.class}`}>{st.label}</span>
                            </td>
                            <td>{formatPosicao(c)}</td>
                            <td>{c.usuario?.nome || c.usuario?.username || '—'}</td>
                            <td>
                              <button
                                type="button"
                                className="ispjp-btn ispjp-btn-secondary"
                                style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
                                onClick={() => {
                                  setSelected(c);
                                  setShowModal(true);
                                }}
                              >
                                Ver
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="ispjp-actions" style={{ justifyContent: 'center' }}>
                    <button
                      type="button"
                      className="ispjp-btn ispjp-btn-secondary"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Anterior
                    </button>
                    <span style={{ color: 'var(--texto-secundario)', alignSelf: 'center' }}>
                      Página {page} de {totalPages}
                    </span>
                    <button
                      type="button"
                      className="ispjp-btn ispjp-btn-secondary"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Próxima
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {showModal && selected && (
          <div
            className="ispjp-modal-overlay"
            onClick={() => setShowModal(false)}
            role="presentation"
          >
            <div
              className="ispjp-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="ispjp-card-header">
                <div className="ispjp-card-header-left">
                  <span className="ispjp-card-header-title">
                    {formatCnpj(selected.cnpj)} · {selected.ordem}
                  </span>
                  <span className={`ispjp-status-pill ${statusConfig(selected.status).class}`}>
                    {statusConfig(selected.status).label}
                  </span>
                </div>
                <button
                  type="button"
                  className="ispjp-btn ispjp-btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Fechar
                </button>
              </div>
              <div className="ispjp-card-body">
                <div className="ispjp-field-grid" style={{ marginBottom: '1rem' }}>
                  <Campo label="Criado em" valor={formatDate(selected.created_at)} />
                  <Campo label="Usuário" valor={selected.usuario?.nome} />
                  <Campo label="Passo" valor={selected.passo} />
                  <Campo
                    label="Posição fila"
                    valor={
                      selected.posicao_fila != null ? `#${selected.posicao_fila}` : null
                    }
                  />
                </div>
                {selected.status === 'error' && (
                  <div className="ispjp-error">{selected.error_message || 'Erro'}</div>
                )}
                {selected.resultado && (
                  <ResultadoDetalhe resultado={selected.resultado} consulta={selected} />
                )}
                {isAdminOrDiretor() && (
                  <div className="ispjp-actions">
                    <button
                      type="button"
                      className="ispjp-btn ispjp-btn-secondary"
                      style={{ color: '#f87171' }}
                      onClick={() => handleDelete(selected.id)}
                    >
                      Excluir consulta
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HistoricoInfoSpyPj;
